import enCa from '@/locales/en-CA/index.js';
import frCa from '@/locales/fr-CA/index.js';

export const LOCALE_MESSAGES = {
    'fr-CA': frCa,
    'en-CA': enCa,
} as const;

export const CONTENT_LANGUAGE_CODES = ['fr', 'en', 'nl'] as const;
export const SUPPORTED_LOCALE_CODES = Object.keys(LOCALE_MESSAGES) as Locale[];

export type ContentLanguage = (typeof CONTENT_LANGUAGE_CODES)[number];
export type Locale = keyof typeof LOCALE_MESSAGES;

export interface ContentLanguageCapabilities {
    email: boolean;
    sms: boolean;
    template: boolean;
    ivr: boolean;
    logo: boolean;
}

export interface ContentLanguageDefinition {
    code: ContentLanguage;
    enabled?: boolean;
    countries?: string[];
    capabilities?: Partial<ContentLanguageCapabilities>;
}

export interface CountryCapabilities {
    geocoding: boolean;
    sms_number_search: boolean;
    sms_number_purchase: boolean;
    toll_free_sms: boolean;
    phone_number_search: boolean;
    phone_number_purchase: boolean;
    toll_free_validation: boolean;
}

export interface SubdivisionDefinition {
    code: string;
    name?: string;
    translation_key?: string | null;
}

export interface PostalCodeInputConfig {
    labelKey: string | null;
    example: string | null;
    mask: string | null;
    casing: 'upper' | 'lower' | 'none';
    maxLength: number | null;
    inputMode: 'text' | 'numeric' | 'decimal' | 'email' | 'tel' | 'search' | 'url';
}

export interface CountryDefinition {
    code: string;
    name: string;
    translation_key?: string | null;
    enabled?: boolean;
    subdivisions?: SubdivisionDefinition[];
    postal_input?: {
        label_key?: string | null;
        example?: string | null;
        mask?: string | null;
        casing?: 'upper' | 'lower' | 'none';
        max_length?: number | null;
        input_mode?: PostalCodeInputConfig['inputMode'];
    };
    timezones?: {
        default?: string | null;
        options?: string[];
        by_subdivision?: Record<string, string>;
    };
    currency?: string | null;
    measurement_system?: 'metric' | 'imperial' | null;
    capabilities?: Partial<CountryCapabilities>;
}

export interface LocaleDefinition {
    code: Locale;
    language: ContentLanguage;
    country: string[];
    bundle: Locale;
    currency: string;
    date_locale: string;
    number_locale: string;
}

export interface LocalizationContract {
    content_languages?: ContentLanguageDefinition[];
    countries?: CountryDefinition[];
    locales?: LocaleDefinition[];
    content_fallback_language?: ContentLanguage | null;
    fallback_locale?: Locale | null;
    fallback_any?: false;
    regional_country?: string | null;
    regional_country_source?: string | null;
}

const LOCALE_BY_LANGUAGE_AND_COUNTRY: Record<ContentLanguage, Partial<Record<string, Locale>>> = {
    fr: { CA: 'fr-CA', US: 'fr-CA' },
    en: { CA: 'en-CA', US: 'en-CA' },
    nl: {},
};

const EMPTY_COUNTRY_CAPABILITIES: CountryCapabilities = {
    geocoding: false,
    sms_number_search: false,
    sms_number_purchase: false,
    toll_free_sms: false,
    phone_number_search: false,
    phone_number_purchase: false,
    toll_free_validation: false,
};

export const isContentLanguage = (language: unknown): language is ContentLanguage =>
    typeof language === 'string' && CONTENT_LANGUAGE_CODES.includes(language as ContentLanguage);

export const isSupportedLocale = (locale: unknown): locale is Locale =>
    typeof locale === 'string' && SUPPORTED_LOCALE_CODES.includes(locale as Locale);

export const contentLanguageForLocale = (locale: Locale): ContentLanguage =>
    locale.slice(0, locale.indexOf('-')) as ContentLanguage;

export const getCountryDefinition = (
    country: unknown,
    contract: LocalizationContract | null | undefined
): CountryDefinition | null => {
    if (typeof country !== 'string') {
        return null;
    }

    const code = country.trim().toUpperCase();

    const findCountry = (value: string) =>
        contract?.countries?.find(
            (definition) =>
                definition.enabled !== false &&
                typeof definition.code === 'string' &&
                definition.code.trim().toUpperCase() === value
        ) ?? null;

    const regionalSource = contract?.regional_country_source?.trim().toUpperCase();

    return (
        findCountry(code) ??
        (regionalSource === code
            ? findCountry(contract?.regional_country?.trim().toUpperCase() ?? '')
            : null)
    );
};

export const normalizeRuntimeCountryCode = (
    country: unknown,
    contract: LocalizationContract | null | undefined
): string | null => getCountryDefinition(country, contract)?.code.toUpperCase() ?? null;

export const localeForContentLanguage = (
    language: ContentLanguage,
    country: string | null | undefined,
    contract?: LocalizationContract | null
): Locale | null => {
    const normalizedCountry = country?.trim().toUpperCase();

    if (!normalizedCountry) {
        return null;
    }

    const matchingLocales = contract?.locales?.filter(
        (locale) =>
            locale.language === language &&
            Array.isArray(locale.country) &&
            locale.country.includes(normalizedCountry)
    );
    const configuredLocale =
        matchingLocales?.find((locale) => locale.code === `${language}-${normalizedCountry}`)
            ?.code ?? matchingLocales?.[0]?.code;

    if (configuredLocale && isSupportedLocale(configuredLocale)) {
        return configuredLocale;
    }

    return LOCALE_BY_LANGUAGE_AND_COUNTRY[language]?.[normalizedCountry] ?? null;
};

export const getCapabilityContentLanguages = (
    contract: LocalizationContract | null | undefined,
    capability: keyof ContentLanguageCapabilities
): ContentLanguageDefinition[] =>
    contract?.content_languages?.filter(
        (language) => language.enabled !== false && language.capabilities?.[capability] === true
    ) ?? [];

export const getCountryCapabilities = (
    country: unknown,
    contract: LocalizationContract | null | undefined
): CountryCapabilities => ({
    ...EMPTY_COUNTRY_CAPABILITIES,
    ...getCountryDefinition(country, contract)?.capabilities,
});

export const supportsPhoneNumberSearch = (capabilities: CountryCapabilities): boolean =>
    capabilities.phone_number_search && capabilities.geocoding;

export const supportsLocalSmsNumberPurchase = (capabilities: CountryCapabilities): boolean =>
    capabilities.sms_number_purchase && capabilities.sms_number_search && capabilities.geocoding;

export const supportsTollFreeSmsNumberPurchase = (capabilities: CountryCapabilities): boolean =>
    capabilities.toll_free_sms && capabilities.sms_number_purchase;

export const getSubdivisions = (
    country: unknown,
    contract: LocalizationContract | null | undefined
): SubdivisionDefinition[] => getCountryDefinition(country, contract)?.subdivisions ?? [];

export const getTimezoneOptions = (
    country: unknown,
    contract: LocalizationContract | null | undefined
): string[] => getCountryDefinition(country, contract)?.timezones?.options ?? [];

export const getDefaultTimezone = (
    country: unknown,
    contract: LocalizationContract | null | undefined
): string | null => getCountryDefinition(country, contract)?.timezones?.default ?? null;

export const getTimezoneForSubdivision = (
    country: unknown,
    subdivision: unknown,
    contract: LocalizationContract | null | undefined
): string | null => {
    if (typeof subdivision !== 'string') {
        return null;
    }

    return (
        getCountryDefinition(country, contract)?.timezones?.by_subdivision?.[
            subdivision.trim().toUpperCase()
        ] ?? null
    );
};

export const getPostalCodeInputConfig = (
    country: unknown,
    contract: LocalizationContract | null | undefined
): PostalCodeInputConfig => {
    const input = getCountryDefinition(country, contract)?.postal_input;

    return {
        labelKey: input?.label_key ?? null,
        example: input?.example ?? null,
        mask: input?.mask ?? null,
        casing: input?.casing ?? 'none',
        maxLength: input?.max_length ?? null,
        inputMode: input?.input_mode ?? 'text',
    };
};

export const getCurrency = (
    country: unknown,
    contract: LocalizationContract | null | undefined
): string | null => getCountryDefinition(country, contract)?.currency ?? null;

export const getMeasurementSystem = (
    country: unknown,
    contract: LocalizationContract | null | undefined
): 'metric' | 'imperial' | null =>
    getCountryDefinition(country, contract)?.measurement_system ?? null;
